/**
 * vitals-chart.component.ts
 *
 * Echarts Options Documentation: https://echarts.apache.org/en/option.html#title
 */

import { Component, ElementRef, inject, input, OnChanges, OnDestroy, OnInit } from '@angular/core';
import { Vital } from 'src/app/calvin-emr-shared/models/vitals.model';
import { EChartsOption } from 'echarts';
import { ECharts } from 'echarts/core';
import { Select } from 'primeng/select';
import { FormsModule } from '@angular/forms';

import { NgxEchartsDirective } from 'ngx-echarts';

@Component({
  selector: 'app-vitals-chart',
  templateUrl: './vitals-chart.component.html',
  styleUrls: ['./vitals-chart.component.scss'],
  standalone: true,
  imports: [
    Select,
    FormsModule,
    NgxEchartsDirective
],
})
export class VitalsChartComponent implements OnInit, OnChanges, OnDestroy {
  options: EChartsOption = {};
  selectedVital = 'Heart Rate';

  data = input<Vital[]>([]);
  viewableVitals = input<string[]>([]);

  displayedData: any = {};

  private chartInstance: ECharts | null = null;
  private observer: IntersectionObserver | null = null;
  private el = inject(ElementRef);

  constructor() { }

  onChartInit(chart: ECharts): void {
    this.chartInstance = chart;
    this.observer = new IntersectionObserver(entries => {
      if (entries[0].isIntersecting) {
        chart.resize();
      }
    });
    this.observer.observe(this.el.nativeElement);
  }

  ngOnDestroy(): void {
    this.observer?.disconnect();
  }

  ngOnInit(): void {
    this.configChart();
  }

  ngOnChanges() {
    this.configChart();
  }

  onViewChange() {
    this.configChart();
  }

  configChart() {
    if (this.data().length > 0) {
      // Filter out deleted vitals
      const filteredVitals = this.data().filter((vital) => !vital.deleted);

      // Get dates as string for x-axis
      const dates = filteredVitals.map((vital) =>
        new Date(vital.date.seconds * 1000).toDateString()
      );

      // Default vital to view is heart rate
      let currentVital = 'heartRate';
      let currentVitalUnit = 'bpm';

      // Instantiate boundaries of data - lower and upper bounds
      //  indicate the boundaries of the normal levels of each vital.
      //  Maximum/minimum indicates the highest/lowest the vital can possibly go (or
      //  be displayed on the chart).
      let currentVitalLowerBound = 0;
      let currentVitalUpperBound = 0;
      let currentVitalMin = 0;
      let currentVitalMax = 0;

      // Get desired data type, corresponding normal levels, and min/max for y-axis
      switch (this.selectedVital) {
        case 'Heart Rate':
          currentVital = 'heartRate';
          currentVitalUnit = 'bpm';
          currentVitalLowerBound = 40;
          currentVitalUpperBound = 100;
          currentVitalMin = 0;
          currentVitalMax = 120;
          break;
        case 'Oxygen Saturation':
          currentVital = 'oxySat';
          currentVitalUnit = '%';
          currentVitalLowerBound = 92;
          currentVitalUpperBound = 100;
          currentVitalMin = 70;
          currentVitalMax = 100;
          break;
        case 'Respirations':
          currentVital = 'respirations';
          currentVitalUnit = '';
          currentVitalLowerBound = 12;
          currentVitalUpperBound = 25;
          currentVitalMin = 0;
          currentVitalMax = 50;
          break;
        case 'Temperature':
          currentVital = 'temp';
          currentVitalUnit = '°C';
          currentVitalLowerBound = 36.1;
          currentVitalUpperBound = 37.2;
          currentVitalMin = 32.2;
          currentVitalMax = 42.2;
          break;
        case 'Systolic BP':
          currentVital = 'systolic';
          currentVitalUnit = 'mm Hg';
          currentVitalLowerBound = 180;
          currentVitalUpperBound = 180;
          currentVitalMin = 100;
          currentVitalMax = 200;
          break;
        case 'Diastolic BP':
          currentVital = 'diastolic';
          currentVitalUnit = 'mm Hg';
          currentVitalLowerBound = 60;
          currentVitalUpperBound = 100;
          currentVitalMin = 0;
          currentVitalMax = 140;
          break;
        default:
          currentVital = '';
          currentVitalUnit = '';
          currentVitalLowerBound = 0;
          currentVitalUpperBound = 100;
          currentVitalMin = 0;
          currentVitalMax = 100;
      }

      const unit = currentVitalUnit == '' ? '' : ' (' + currentVitalUnit + ')';

      // Get specific property from data based on user's selection
      const dataset = filteredVitals.map((vital) => vital[currentVital as keyof Vital] as number);

      this.setDisplayedData(dates, dataset);

      // Initialize chart options
      this.options = {
        xAxis: {
          type: 'category',
          name: 'Date',
          nameLocation: 'end',
          data: dates,
        },
        yAxis: {
          type: 'value',
          name: this.selectedVital + unit,
          nameLocation: 'end',
          min: currentVitalMin,
          max: currentVitalMax,
        },
        tooltip: {
          trigger: 'axis',
          formatter: (params: any) => {
            params = params[0];
            return params.name + ' : ' + params.value + currentVitalUnit;
          },
          axisPointer: {
            animation: false,
          },
        },
        series: [
          {
            data: dataset,
            type: 'line',
            // Boundary lines for vital
            markLine: {
              data: [
                { name: 'Upper', yAxis: currentVitalUpperBound },
                { name: 'Lower', yAxis: currentVitalLowerBound },
              ],
              lineStyle: {
                color: 'red',
              },
              // Remove arrow at the end of the line
              symbol: ['none', 'none'],
            },
          },
        ],
      };
    }
  }

  setDisplayedData(labels: string[], datasets: number[]) {
    this.displayedData = {
      labels: labels,
      datasets: datasets,
    };
  }

  get hasData(): boolean {
    return this.data().length > 0;
  }
}
